<template>
    <v-card class="pa-5 mb-3 max-card">
        <div class="d-flex">
            <h1>Annual summary</h1>
            <v-spacer></v-spacer>
            <v-btn @click="handleSendEmail" small color="primary">Send email</v-btn>
        </div>
        <p>Here you can define formulas for Annual summary. Recalculate calculates previous week data again. Save button
            saves current formula and after request list refresh is triggered what means that the other formula's
            unsaved changes will be deleted.</p>

        <v-divider class="mb-4 mt-3"/>

        <h3>Formula types</h3>
        <p>We have three types of formulas:</p>
        <p>
            <strong>Value</strong> - returns always value as a result. Also works with 'Has graph' prop. If the 'Has
            graph' toggle is disabled then it calculates as period summary, if it is toggled then it calculates per day.
        </p>
        <p>
            <strong>Boolean</strong> - Similar to Recommendation calculation, returns true or false. Works only per day,
            no summary period accepted here. Also 'Has graph' wont too anything with this prop.
        </p>
        <p>
            <strong>Recommendation</strong> - Counts recommendations per day. If day has multiple same recommendation
            then the answer is counted as 1.
        </p>
        <v-divider class="mb-4 mt-3"/>
        <h3>Formula answers</h3>
        <p>Formula answer depends on the selected formula type. </p>
        <p>
            <strong>Value</strong> - There you can not edit the answer. The answer will be always the formula result.
        </p>
        <p>
            <strong>Boolean and recommendation</strong> - There you can use boolean answer - that means if formula
            answer is "yes"/"no" or bigger than 0 then you can define custom string that you want to show for client.
            Ex. you have frozen formula where there are multiple attributes and you can't show exact number as result
            then you should type "Frozen" into Yes field. If the formula has "Ignore zero values" property active then
            the "no" statement part is not needed.
        </p>
        <p>
            <strong>Boolean and recommendation</strong> - There you can use also "Show variable as result". From that
            you can show answer as result. Ex. you have recommendation related with soil water and then you want to show
            out the soil water then you can show the result as sensor_low.Er. Show variable as result at the moment
            returns ALWAYS average of answer period. In this input you should always use the attributes which are
            mentioned below as ""Boolean" formula attribute".
        </p>
        <v-divider class="mb-4 mt-3"/>
        <h2>When to use "Value", "Boolean" or "Recommendation"</h2>
        <p><strong>Value</strong> - If you want to get answer from data array which are coming from database. Ex.
            np.sum(np.array(weather.night_lowest_temps) > 0) - it takes all weather.night_lowest_temps from database and
            counts which one of them are bigger than zero. Result is as count of night_lowest_temp attributes where
            value is bigger than 0. Ex. np.average(station_daily_temperature.yearly_Ets) - all day measurements - [1.2,
            1.4, 1.3, 1.5] and np.average() will take the average of them.</p>
        <p><strong>Boolean</strong> - If your statement uses and/or word in formula or your formula answer can only be
            Yes or No. Ex. (sensor_high.Er > 10) and (sensor_high.Er > 20)</p>
        <p><strong>Recommendation</strong> - If you have recommendation already configured and want to show this then
            you can use this type. With that you can specify country, culture and soil type at the moment. It match the name with recommendation indexes.</p>

        <v-divider class="mb-4 mt-3"/>

        <h2>Ground sensors</h2>
        <h3>Sensor attributes with "Value" formula attribute:</h3>
        <p>
            Available data arrays: <strong>Ers, js, Tms and ECes</strong>. These valiables contains values of the
            selected period ex. [1, 1.2, 1.1, 1.3].
        </p>
        <p>
            The following variables require a prefix indicating the sensor: <strong>sensor_low</strong>,
            <strong>sensor_high</strong>, <strong>sensor_both</strong>
        </p>
        <p>
            Example: <strong>np.average(sensor_low.Ers)</strong> or <strong>np.sum(sensor_high.Ers)</strong> or
            <strong>np.min(sensor_both.Ers)</strong>
        </p>

        <h3>Sensor attributes with "Boolean" formula attribute:</h3>
        <p>
            Available data arrays: <strong>Er, j, Tm and ECe</strong>. These variables contains specific average value
            at specific date.
        </p>
        <p>
            The following variables require a prefix indicating the sensor: <strong>sensor_low</strong>,
            <strong>sensor_high</strong>, <strong>sensor_both</strong>
        </p>
        <p>Example: <strong>7 > sensor_high.Er and 5 > sensor_high.j and 0.5 > sensor_high.Tm</strong></p>
        <v-divider class="mb-4 mt-3"/>

        <h2>Air sensor</h2>
        <h3>Air sensor attributes with "Value" formula attribute:</h3>
        <p>
            Available data arrays: <strong>Temps</strong>. This valiable contains values of the selected period ex. [1,
            1.2, 1.1, 1.3].
        </p>
        <p>Following variables require a <strong>sensor_air</strong> prefix: <strong>Temps</strong></p>
        <p>Example: <strong>np.max(sensor_air.Temps)</strong> or <strong>np.average(sensor_air.Temps)</strong></p>

        <h3>Air sensor attributes with "Boolean" formula attribute:</h3>
        <p>
            Available data arrays: <strong>Temp</strong>. This variable contains specific average value at specific
            date.
        </p>
        <p>Following variables require a <strong>sensor_air</strong> prefix: <strong>Temp</strong></p>
        <p>Example: <strong>7 > sensor_air.Temp</strong></p>
        <v-divider class="mb-4 mt-3"/>

        <h2>Station Daily Temperatures</h2>
        <h3>Station Daily Temperatures with "Value" formula attribute:</h3>
        <p>Available data arrays: <strong>Ets, Ats, yearly_Ets, yearly_Ats</strong>.</p>
        <p>
            Following variables require either a <strong>station_daily_temperature</strong> prefix:
            <strong>Ets, Ats, yearly_Ets, yearly_Ats</strong>
        </p>
        <p>There are usually only one database entry per day but you must use numpy function as wrapper.</p>
        <p>
            Example: <strong>np.average(station_daily_temperature.yearly_Ets)</strong> or
            <strong>np.average(station_daily_temperature.yearly_Ats)</strong>. Yearly means the summed amount (ex.
            2024-05-12 12.2, 2024-05-13 15.6 etc), without yearly prefix gives you excact value for current daty wiout
            summing.
        </p>

        <h3>Station Daily Temperatures with "Boolean" formula attribute:</h3>
        <p>
            Available data arrays:
            <strong>Det (Daily effective temperature), Dat (Daily active temperature), yearly_Det, yearly_Dat</strong>.
        </p>
        <p>
            Following variables require either a <strong>station_daily_temperature</strong> prefix:
            <strong>Det, Dat, yearly_Det, yearly_Dat</strong>
        </p>
        <p>Example: <strong>7 > station_daily_temperature.Det and 5 > station_daily_temperature.Dat</strong></p>
        <v-divider class="mb-4 mt-3"/>

        <h2>Weather</h2>
        <h3>Weather with "Value" formula attribute:</h3>
        <p>Available data arrays: <strong>night_lowest_temps</strong>.</p>
        <p>
            Following variables require either a <strong>weathers</strong> prefix: <strong>night_lowest_temps</strong>
        </p>
        <p>There are usually only one database entry per day but you must use numpy function as wrapper.</p>
        <p>Example: <strong>np.average(weather.night_lowest_temps)</strong></p>

        <h3>Weather with "Boolean" formula attribute:</h3>
        <p>Available data arrays: <strong>night_lowest_temp</strong>.</p>
        <p>Following variables require either a <strong>weather</strong> prefix: <strong>night_lowest_temp</strong></p>
        <p>Example: <strong>0 > weather.night_lowest_temp</strong></p>

        <v-divider class="mb-4 mt-3"/>

        <p>
            We are using for calculations <a href="https://numpy.org/">NumPy</a> library. First add
            <strong>np.</strong> prefix and then the function name.
        </p>
        <p>
            Example: <strong>np.average()</strong>, <strong>np.sum()</strong>, <strong>np.min()</strong>,
            <strong>np.max()</strong> etc. You can see the functions from
            <a href="https://numpy.org/doc/stable/reference/routines.math.html">here</a>.
        </p>
    </v-card>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'AnnualSummaryInfoCardComponent',
    methods: {
        ...mapActions('annualSummary', ['sendAnnualSummaryEmail']),
        async handleSendEmail() {
            await this.sendAnnualSummaryEmail()
        }
    }
};
</script>

<style scoped lang="scss">
.max-card {
    max-height: 500px;
    overflow-y: auto;
}

p {
    margin-bottom: 8px;
}
</style>
